<script setup>
/* Imports */
import {
  ref,
  onMounted,
  onBeforeUnmount,
  useTemplateRef,
  watch,
} from 'vue';

/* Components */
import Awesomplete from 'awesomplete';

/* Props */
const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  label: {
    type: String,
    required: true,
  },
  placeholder: {
    type: String,
    default: '',
  },
  settings: {
    type: Object,
    default: () => ({}),
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  modelValue: {
    type: String,
    default: '',
  },
});

/* State */
const state = ref({
  awesomplete: null, // Awesomplete Instance
});

/* Emits */
const emits = defineEmits([
  'select',
  'selectcomplete',
  'open',
  'close',
  'highlight',
  'update:model-value',
]);

/* Ref */
const templateRefs = {
  autocompleteInput: useTemplateRef('autocomplete-input'),
};

/* Expose */
defineExpose({
  focusInput: () => templateRefs.autocompleteInput?.value?.focus(),
});

onMounted(() => {
  const input = templateRefs.autocompleteInput?.value;

  if (input) {
    input.value = props.modelValue;
  }

  const defaultSettings = {
    minChars: 3,
  };
  const customSettings = props.settings;
  const settings = {
    ...defaultSettings,
    ...customSettings,
  };

  state.value.awesomplete = new Awesomplete(input, settings);

  watch(
    () => props.settings?.list,
    (newList) => {
      if (state.value?.awesomplete) {
        // Update the Autocomplete list dynamically
        // Ensures it stays in sync with the reactive store and prevents duplicates rendering
        state.value.awesomplete.list = newList;
      }
    },
    { immediate: true },
  );

  input.addEventListener('awesomplete-select', (e) => {
    emits('select', e.text.value);
    input.value = ''; // Clear the input field
  });

  input.addEventListener('awesomplete-selectcomplete', (e) => {
    emits('selectcomplete', e.text.value);
    emits('update:model-value', ''); // Clear the input field
  });

  input.addEventListener('awesomplete-open', () => {
    emits('open');
  });

  input.addEventListener('awesomplete-close', () => {
    emits('close');
  });

  input.addEventListener('awesomplete-highlight', () => {
    emits('highlight');
  });

  input.addEventListener('input', (e) => {
    emits('update:model-value', e?.target?.value);
  });

  input.addEventListener('focus', () => {
    state.value.awesomplete?.evaluate();
  });
});

/* Destroyed */
onBeforeUnmount(() => {
  state.value.awesomplete?.destroy();
});
</script>

<template>
  <div
    class="autocomplete"
    data-testid="autocomplete"
  >
    <g-input
      :label="props.label"
      :label-for="props.id"
      hide-label="true"
      :dataTestId="{
        host: 'autocomplete-input',
        label: 'autocomplete-input-label',
        wrapper: 'autocomplete-input-wrapper',
      }"
    >
      <!-- eslint-disable vue/no-deprecated-slot-attribute -->
      <div slot="prepend">
        <g-icon-search
          class="g-icon-search"
          data-testid="autocomplete-icon-search"
        />
      </div>
      <!-- eslint-enable vue/no-deprecated-slot-attribute -->
      <input
        :id="props.id"
        ref="autocomplete-input"
        :placeholder="props.placeholder"
        type="text"
        data-testid="autocomplete-product-search-input"
        :disabled="props.disabled"
      >
    </g-input>
  </div>
</template>

<style lang="scss">
@import '../../node_modules/awesomplete/awesomplete.css';

[slot = 'prepend'] {
  width: 0;
}

.g-icon-search {

  svg {
    position: relative;
    z-index: 1;
    width: 1.5rem;
    height: 1.5rem;
    left: 0.5rem;
    fill: $color-black;
    padding-top: 0.2rem;
  }
}

// awesomplete overrides
.awesomplete {
  color: inherit;
  width: 100%;

  > ul {
    background: $color-white;
    border-radius: 0;
    @include drop-shadow();
    top: auto;
    bottom: 100%;
    margin-bottom: 0.25rem;
    border: 1px $color-black solid;

    // Dropdown results goes below the input
    @include breakpoint('sm') {
      top: 100%;
      bottom: auto;
    }

    /* Pointer */
    &::before {
      display: none;
    }

    > li {
      cursor: pointer;
      list-style: none;
      padding: 0.6rem 1rem;
      margin: 0;
      position: relative;
      text-align: left;

      &:hover {
        background: $color-black;
        color: $color-white;
      }

      &[aria-selected = 'true'] {
        background: $color-black;
        color: $color-white;
      }
    }
  }

  /* Represents the text that matches what the user has typed */

  mark {
    background: none;
  }

  li {

    &:hover {

      mark {
        background: transparent;
        color: $color-white;
      }
    }

    &[aria-selected = 'true'] {

      mark {
        background: transparent;
        color: $color-white;
      }
    }
  }
}

.autocomplete {

  .g__input input {
    line-height: 1.42;
    padding-left: 2.5rem; // Search icon padding
  }

  .g__input__wrapper {
    margin: 0;
  }
}
</style>
